import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import qs from 'query-string';
import { dataCentralApi } from '@/api/client';
import { toFilters } from '@/api/customers';
import type { ResponseArray, ResponseObject } from '@/types/shared';

/**
 * Customer contracts tab — `GET /customers/:id/contracts` (§4.5). Field names are
 * the backend `GetContractsByCustomerIdRow` verbatim. Server-paginated; filters
 * (product_group_name / contract_status / contract_role / contract_direction) go
 * base64 into the `filters` query param.
 */

export interface ContractRow {
  contract_id: number;
  contract_number_id: string;
  contract_status: string;
  contract_role?: string;
  contract_direction?: string;
  contract_value?: number;
  contract_expiry_date?: string;
  contract_effective_date?: string;
  created_at?: string;
  service_id?: number;
  service_name?: string;
  product_group_name?: string;
  partner_id?: number;
  partner_name?: string;
  partner_code?: string;
  partner_email?: string;
  partner_phone?: string;
}

export interface ContractsParams {
  page?: number;
  page_size?: number;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
  filters?: Record<string, unknown>;
}

function contractsQuery(params: ContractsParams): string {
  const { filters, ...rest } = params;
  const q: Record<string, string | number> = {};
  for (const [k, v] of Object.entries(rest)) {
    if (v !== undefined && v !== '') q[k] = v as string | number;
  }
  if (filters && Object.keys(filters).length > 0) q.filters = toFilters(filters);
  return qs.stringify(q);
}

/** `GET /customers/:id/contracts` — the contracts table for one customer. */
export function useCustomerContracts(id: number, params: ContractsParams = {}) {
  return useQuery({
    queryKey: ['customers', 'contracts', id, params],
    enabled: !!id,
    queryFn: async (): Promise<ResponseArray<ContractRow>> => {
      const res = await dataCentralApi.get<ResponseArray<ContractRow>>(
        `/customers/${id}/contracts?${contractsQuery({ page: 1, page_size: 20, ...params })}`,
      );
      return res.data;
    },
  });
}

/* ── Mutations ─────────────────────────────────────────────────────────── */

/**
 * `POST /contracts/customers/merged` — merge a list of contracts (non-active)
 * into a primary contract. Backend requires `contract_id` (primary) + `customer_id`
 * + `merge_contract_ids` (the contracts to absorb). Verified route reachable live
 * (returns 400 on missing contract_id, not 404).
 */
export function useMergeContracts(customerId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      contract_id: number;
      merge_contract_ids: number[];
      contract_reason?: string;
    }): Promise<ResponseObject<unknown>> => {
      const res = await dataCentralApi.post<ResponseObject<unknown>>(
        '/contracts/customers/merged',
        { customer_id: customerId, ...vars },
      );
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['customers', 'contracts', customerId] });
    },
  });
}
